import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { makeLabeler, useCommonCodes, type CodeLabeler } from '../lib/codes'
import { adminApi, can, type MatchCandidate, type MatchRunSummary } from '../lib/adminApi'

/**
 * 매칭 결과 — 실행 · 후보 · 근거 · 상태 전이.
 *
 * **점수를 확률로 읽히게 쓰지 않는다**(설계 원칙 §4-5). "적합도 82점"은 쓰고
 * "해결 가능성 82%"는 쓰지 않는다. 등급(낮음·중간·높음)은 회의 결정에 따라 병행 표기한다.
 *
 * **근거는 서버가 저장한 것을 그대로 그린다.** 화면이 다시 계산하면 배점을 바꾼 뒤
 * 과거 실행을 열었을 때 점수와 근거가 어긋난다.
 */

const SCORING_ITEM = 'scoring_item'
const EXCLUSION_REASON = 'exclusion_reason'

/** 정본은 용어 통일표 §3(적합도 등급)이다. `fit_grade` 값에 붙는 표시용 이름이다. */
const GRADE_LABEL: Record<string, string> = {
  low: '낮음',
  mid: '중간',
  high: '높음',
}

/**
 * 커버리지·확실성 칩 라벨(개편안 R-5) — 등급의 **근거 축**이라 GRADE_LABEL과 같은 자리다.
 * common_code가 아니라 로컬 맵인 이유도 같다: 값 집합이 코드(matching.ts 타입)에 봉인돼
 * 있고 운영자가 늘리는 값이 아니다. 모르는 값은 원문 그대로 그린다(감춤 금지).
 */
const COVERAGE_LABEL: Record<string, string> = {
  full: '필요 자원 전부 충족',
  wider: '필요 자원 이상 보유',
  partial: '필요 자원 일부만',
  none: '필요 자원 무관',
}
const CERTAINTY_LABEL: Record<string, string> = {
  certain: '판정 확실',
  uncertain: '판정 불확실',
}
const CONDITION_LABEL: Record<string, string> = {
  region: '지역',
  category: '분야',
  target: '대상',
  timing: '시점',
}

/** 코드 라벨은 테이블에서 온다 — 두 그룹을 함께 넘겨 하위 컴포넌트가 다시 조회하지 않게 한다. */
interface CodeLabelers {
  scoringItem: CodeLabeler
  exclusionReason: CodeLabeler
}

/**
 * 항목 점수는 비율 계산에서 나오므로 `1.1111111111111112` 같은 값이 그대로 온다.
 * **서버가 저장한 값은 반올림하지 않는다** — 재현·감사용 원값이라 그대로 둬야 하고,
 * 화면에서만 소수 1자리로 줄인다. 소수 1자리면 손 검산에 충분하다.
 */
function formatPoints(n: number | null): string {
  // 점수가 없는 것과 0점은 다르다 — 없으면 숫자 자리에 0을 그리지 않는다.
  if (n === null) return '—'
  return String(Math.round(n * 10) / 10)
}

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    if (error.status === 422) return error.detail[0] ?? '제외된 후보는 상태를 바꿀 수 없습니다.'
    if (error.status === 409) return error.detail[0] ?? '이미 다른 상태로 바뀐 항목입니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminMatchRunPage() {
  const { id: problemCardId } = useParams<{ id: string }>()
  const me = useAdminMe()
  const queryClient = useQueryClient()
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)

  const mayRun = can(me.data, 'card', 'write')
  // 결과 공개는 검수 공개와 같은 급이라 별도 권한이다(admin-match.ts — card.publish).
  const mayPublish = can(me.data, 'card', 'publish')

  const runs = useQuery({
    queryKey: ['admin-match-runs', problemCardId],
    queryFn: () => adminApi.matchRuns(problemCardId ?? ''),
    enabled: problemCardId !== undefined,
  })
  const runItems: MatchRunSummary[] = runs.data?.items ?? []
  // 고르지 않았으면 가장 최근 실행을 본다 — 매번 클릭하게 만들 이유가 없다.
  const currentRunId = selectedRunId ?? runItems[0]?.id ?? null

  const execute = useMutation({
    mutationFn: () => adminApi.runMatch(problemCardId ?? ''),
    onSuccess: (created) => {
      setSelectedRunId(created.id)
      void queryClient.invalidateQueries({ queryKey: ['admin-match-runs', problemCardId] })
    },
  })

  if (problemCardId === undefined) return null

  return (
    <div>
      <Link className="back-link" to="/admin/match">
        ← 매칭 목록
      </Link>

      <div className="admin-bar">
        <div>
          <h1 className="page-title">매칭 결과</h1>
          <p className="page-desc">
            공개 상태인 해결카드만 후보가 됩니다. 실행할 때마다 결과가 새로 쌓이며 이전 결과는
            지워지지 않습니다.
          </p>
        </div>
        <div className="admin-who">
          <button
            type="button"
            className="btn"
            disabled={!mayRun || execute.isPending}
            onClick={() => execute.mutate()}
          >
            {execute.isPending ? '실행 중…' : '매칭 실행'}
          </button>
        </div>
      </div>

      {!mayRun && (
        <div className="notice">
          읽기 권한만 있는 계정입니다. 매칭 실행과 상태 변경은 <b>card.write</b> 권한이 있어야
          합니다.
        </div>
      )}

      {execute.error !== null && (
        <div className="notice error" role="alert">
          {errorMessage(execute.error)}
        </div>
      )}

      <QueryState
        isPending={runs.isPending}
        error={runs.error}
        isEmpty={runItems.length === 0}
        emptyMessage="아직 실행한 적이 없습니다. '매칭 실행'을 누르면 결과가 생깁니다."
      >
        <div className="run-history">
          {runItems.map((r) => (
            <button
              key={r.id}
              type="button"
              className={r.id === currentRunId ? 'active' : undefined}
              onClick={() => setSelectedRunId(r.id)}
            >
              <b>{new Date(r.executedAt).toLocaleString('ko-KR')}</b>
              <span className="muted small">
                배점 {r.weightConfigVersion} · 후보 {r.candidateCount} · 제외 {r.excludedCount}
              </span>
            </button>
          ))}
        </div>
      </QueryState>

      {currentRunId !== null && (
        <RunResult runId={currentRunId} mayRun={mayRun} mayPublish={mayPublish} />
      )}
    </div>
  )
}

function RunResult(props: { runId: string; mayRun: boolean; mayPublish: boolean }) {
  const queryClient = useQueryClient()
  const scoringCodes = useCommonCodes(SCORING_ITEM)
  const exclusionCodes = useCommonCodes(EXCLUSION_REASON)
  const labelers: CodeLabelers = {
    scoringItem: makeLabeler(scoringCodes.data),
    exclusionReason: makeLabeler(exclusionCodes.data),
  }

  const run = useQuery({
    queryKey: ['admin-match-run', props.runId],
    queryFn: () => adminApi.matchRun(props.runId),
  })

  const transition = useMutation({
    mutationFn: (v: { candidateId: string; to: string }) =>
      adminApi.setMatchStatus(v.candidateId, v.to),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-match-run', props.runId] }),
  })

  // 결과 공개(025 — 2단계 게이트의 1단계). run 목록도 공개 표식을 그리므로 함께 갱신한다.
  const publish = useMutation({
    mutationFn: (published: boolean) => adminApi.setMatchResultPublish(props.runId, published),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-match-run', props.runId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-match-runs'] })
    },
  })

  const candidates = (run.data?.candidates ?? []).filter((c) => !c.excluded)
  const excluded = (run.data?.candidates ?? []).filter((c) => c.excluded)

  return (
    <QueryState isPending={run.isPending} error={run.error}>
      {run.data !== undefined && (
        <>
          <dl className="kv">
            <dt>문제</dt>
            <dd>{run.data.problemCard.title}</dd>
            <dt>배점 · 알고리즘</dt>
            <dd>
              {run.data.weightConfigVersion} · {run.data.algorithmVersion}
            </dd>
            <dt>후보 · 제외</dt>
            <dd>
              후보 {run.data.candidateCount}건 · 제외 {run.data.excludedCount}건
              {run.data.durationMs !== null && (
                <span className="muted small"> · {run.data.durationMs}ms</span>
              )}
            </dd>
            <dt>의미유사도</dt>
            <dd className="muted small">
              {run.data.semanticApplied
                ? '적용'
                : '미적용 — 이번 실행은 규칙 기반입니다(임베딩은 확정요청서 B-2 대기).'}
            </dd>
            <dt>결과 공개</dt>
            <dd>
              {run.data.resultPublished ? (
                <>
                  <span className="badge">공개 중</span>
                  {run.data.resultPublishedAt !== null && (
                    <span className="muted small">
                      {' '}
                      {new Date(run.data.resultPublishedAt).toLocaleString('ko-KR')} 승인
                    </span>
                  )}
                </>
              ) : (
                <span className="muted small">
                  미공개 — 시민 화면에는 이 실행의 결과가 나가지 않습니다.
                </span>
              )}
              {props.mayPublish && (
                <button
                  type="button"
                  className="btn ghost small-btn"
                  disabled={publish.isPending}
                  onClick={() => publish.mutate(!run.data!.resultPublished)}
                >
                  {run.data.resultPublished ? '공개 해제' : '결과 공개'}
                </button>
              )}
            </dd>
          </dl>

          {publish.error !== null && (
            <div className="notice error" role="alert">
              {errorMessage(publish.error)}
            </div>
          )}

          {transition.error !== null && (
            <div className="notice error" role="alert">
              {errorMessage(transition.error)}
            </div>
          )}

          {run.data.noMatch && (
            <div className="notice">
              맞는 해결카드가 없습니다. 이 결과도 <b>미충족</b>으로 기록돼 지도·집계의 근거가
              됩니다.
            </div>
          )}

          <h2 className="section-title">후보 {candidates.length}건</h2>
          {candidates.length === 0 ? (
            <p className="muted small">점수를 매길 수 있는 후보가 없습니다.</p>
          ) : (
            <ul className="match-list">
              {candidates.map((c) => (
                <CandidateRow
                  key={c.id}
                  candidate={c}
                  labelers={labelers}
                  mayRun={props.mayRun}
                  isBusy={transition.isPending}
                  onTransition={(to) => transition.mutate({ candidateId: c.id, to })}
                />
              ))}
            </ul>
          )}

          {excluded.length > 0 && (
            <>
              <h2 className="section-title">제외 {excluded.length}건</h2>
              <p className="muted small">
                제외된 카드에는 점수를 매기지 않습니다 — 빈 점수는 0점이 아니라 &quot;매기지
                않았다&quot;는 뜻입니다.
              </p>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>해결카드</th>
                    <th>기업</th>
                    <th>제외 사유</th>
                  </tr>
                </thead>
                <tbody>
                  {excluded.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/admin/cards/${c.solutionCard.id}`}>{c.solutionCard.title}</Link>
                      </td>
                      <td className="muted small">{c.orgName}</td>
                      <td>
                        {c.exclusionReasonCodes.map((code) => (
                          <span key={code} className="badge">
                            {labelers.exclusionReason(code)}
                          </span>
                        ))}
                        {/* AI 거부(R-3)는 사유 텍스트가 함께 남는다 — 판정 경위를 표에서 바로 본다. */}
                        {c.rationaleText !== null && (
                          <div className="muted small">{c.rationaleText}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </QueryState>
  )
}

function CandidateRow(props: {
  candidate: MatchCandidate
  labelers: CodeLabelers
  mayRun: boolean
  isBusy: boolean
  onTransition: (to: string) => void
}) {
  const [open, setOpen] = useState(false)
  const c = props.candidate
  const detail = c.scoreDetail

  return (
    <li className="match-row">
      <div className="match-head">
        <span className="match-rank">{c.rank}</span>
        <div className="match-title">
          <Link to={`/admin/cards/${c.solutionCard.id}`}>{c.solutionCard.title}</Link>
          <div className="muted small">{c.orgName}</div>
        </div>
        <div className="match-score">
          {/* 점수 뒤에 반드시 '점'을 붙인다. %로 쓰면 확률로 읽힌다(설계 원칙 §4-5). */}
          <b>적합도 {c.fitScore}점</b>
          {c.fitGrade !== null ? (
            <span className={`badge grade-${c.fitGrade}`}>
              {GRADE_LABEL[c.fitGrade] ?? c.fitGrade}
            </span>
          ) : (
            c.coverageKind === null &&
            c.certainty !== null && (
              // v3 판정불가 행(need 미입력) — 등급을 지어내지 않고 상태를 그대로 말한다.
              <span className="badge">등급 판정불가</span>
            )
          )}
          {c.coverageKind !== null && (
            <span className="badge">{COVERAGE_LABEL[c.coverageKind] ?? c.coverageKind}</span>
          )}
          {c.certainty !== null && (
            <span className="badge" title={
              c.uncertainConditionCodes.length > 0
                ? `미판정 조건: ${c.uncertainConditionCodes
                    .map((code) => CONDITION_LABEL[code] ?? code)
                    .join(' · ')}`
                : undefined
            }>
              {CERTAINTY_LABEL[c.certainty] ?? c.certainty}
            </span>
          )}
        </div>
        <div className="match-status">
          <span>{c.statusLabel}</span>
          {props.mayRun ? (
            <div className="row-actions">
              {c.allowedNext.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className="btn ghost small-btn"
                  disabled={props.isBusy}
                  onClick={() => props.onTransition(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ) : (
            <span className="muted small">—</span>
          )}
        </div>
        <button type="button" className="btn ghost small-btn" onClick={() => setOpen(!open)}>
          {open ? '근거 접기' : '근거 보기'}
        </button>
      </div>

      {c.unknownAxisCodes.length > 0 && (
        <p className="muted small">
          확인 필요 {c.unknownAxisCodes.length}항목 —{' '}
          {c.unknownAxisCodes.map(props.labelers.scoringItem).join(' · ')}
        </p>
      )}

      {c.rationaleText !== null && (
        <p className="muted small">
          {/* rationale_model이 있으면 실판정 사유, 없으면 «AI 미판정(…)» 보류 표식 그대로다. */}
          {c.rationaleModel !== null ? `AI 판정 — ${c.rationaleText}` : c.rationaleText}
        </p>
      )}

      {open && (
        <div className="match-detail">
          <table className="admin-table">
            <thead>
              <tr>
                <th>항목</th>
                <th>배점</th>
                <th>판정</th>
                <th>근거</th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((i) => (
                <tr key={i.code}>
                  <td>{props.labelers.scoringItem(i.code)}</td>
                  <td>{i.weight}</td>
                  <td>
                    {i.status === 'unknown' ? (
                      <span className="muted">확인 필요</span>
                    ) : (
                      <b>{formatPoints(i.points)}</b>
                    )}
                  </td>
                  <td className="muted small">{i.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* v2 run은 재정규화(분모=잰 항목 합) 시절 저장값이라 설명도 그 식을 따라야 손 검산이 맞는다. */}
          {detail.algorithmVersion === 'v2-rule-resource-axis' ? (
            <p className="muted small">
              확인 필요 항목은 0점이 아니라 <b>분모에서 빠집니다</b>. 이번 계산의 분모는{' '}
              {detail.scoredWeightSum}이고, 얻은 점수 {formatPoints(detail.rawPointSum)}을 100점 기준으로
              환산해 적합도 {c.fitScore}점이 됩니다.
            </p>
          ) : (
            <p className="muted small">
              <b>분모는 항상 100입니다.</b> 이번에 잰 항목의 배점 합은 {detail.scoredWeightSum}이고,
              얻은 점수 {formatPoints(detail.rawPointSum)}을 반올림한 값이 적합도 {c.fitScore}점입니다.
              확인 필요 항목은 점수를 더하지 못하므로 <b>정보를 채우면 점수가 오릅니다</b> —
              그래도 탈락은 아닙니다(§4-4는 등급·협의 큐가 지킵니다).
            </p>
          )}
          {detail.inapplicableExclusions.length > 0 && (
            <p className="muted small">
              판정하지 못한 제외조건:{' '}
              {detail.inapplicableExclusions.map(props.labelers.exclusionReason).join(' · ')} —
              문제카드에 해당 항목이 구조화돼 있지 않아 이번 실행에서는 거르지 못했습니다.
            </p>
          )}
        </div>
      )}
    </li>
  )
}
